/**
 * On-Call Timeline View - Type Definitions
 */

import { OncallAssignmentResponse } from './response'

// Layer types matching Atlassian Opsgenie
export type TimelineLayer = 'final' | 'override' | 'base'

// View modes for assignment list
export type AssignmentViewMode = 'list' | 'timeline'

// Timeline bar data for ECharts
export interface TimelineBarItem {
    name: string
    value: [number, number, number, number] // [rowIndex, startTime, endTime, _originalEnd]
    employeeId: number
    employeeName: string
    assignmentId: number
    scheduleId: number
    scheduleName: string
    layer: TimelineLayer
    status: string
    itemStyle: {
        color: string
    }
    // Source tracking fields
    createdBy: number | null
    createdByName: string | null
    // Swap-related fields (optional, present when layer === 'override')
    swapId?: number
    swapStatus?: string
    swapReason?: string
    requesterEmployeeId?: number
    requesterName?: string
}

// Grouped assignments by layer
export interface LayeredAssignments {
    final: OncallAssignmentResponse[]
    override: OncallAssignmentResponse[]
    base: OncallAssignmentResponse[]
}

// Date range for timeline navigation
export interface TimelineDateRange {
    start: string // YYYY-MM-DD
    end: string // YYYY-MM-DD
}

// Week range options
export type WeekRangeOption = '1_week' | '2_weeks' | '4_weeks'

export const WEEK_RANGE_OPTIONS: {
    value: WeekRangeOption
    label: string
    days: number
}[] = [
    { value: '1_week', label: '1 week', days: 7 },
    { value: '2_weeks', label: '2 weeks', days: 14 },
    { value: '4_weeks', label: '4 weeks', days: 28 },
]

// Timeline chart configuration
export interface TimelineChartConfig {
    startDate: string
    endDate: string
    layers: TimelineLayer[]
    data: TimelineBarItem[]
}

// Override form data (Phase 1: local state only)
export interface OverrideFormData {
    scheduleId: number
    assignmentId?: number
    employeeId: number
    startDatetime: string
    endDatetime: string
    reason?: string
}

// Local override storage (Phase 1)
export interface LocalOverride extends OverrideFormData {
    id: string // UUID for local tracking
    employeeName: string
    createdAt: string
}

// Timeline header props
export interface TimelineHeaderProps {
    dateRange: TimelineDateRange
    weekRange: WeekRangeOption
    onDateRangeChange: (range: TimelineDateRange) => void
    onWeekRangeChange: (range: WeekRangeOption) => void
    onNavigate: (direction: 'prev' | 'next') => void
    onTakeOncall?: () => void
    onGenerateAssignments?: () => void
    scheduleId?: number
    scheduleName?: string
}

// Employee legend item
export interface EmployeeLegendItem {
    employeeId: number
    employeeName: string
    color: string
}

// Schedule group for multi-schedule timeline
export interface ScheduleGroup {
    scheduleId: number
    scheduleName: string
    timezone?: string
    assignments: TimelineBarItem[]
    isExpanded?: boolean
}
